import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../config/firebase';
import { collection, query, getDocs, where } from 'firebase/firestore';
import './Metrics.css';

const unitLogo = '/images/LP_Unit_logo.png';

function Metrics() {
  const { isAdmin, logout } = useAuth();
  const [requests, setRequests] = useState([]);
  const [armorers, setArmorers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      // Fetch requests
      const requestsSnap = await getDocs(collection(db, 'requests'));
      setRequests(requestsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
      // Fetch armorers
      const armorersQuery = query(
        collection(db, 'users'),
        where('role', 'in', ['armorer', 'admin'])
      );
      const armorersSnap = await getDocs(armorersQuery);
      setArmorers(armorersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
      setLoading(false);
    };
    fetchData();
  }, []);

  // Calculate metrics
  const completedRequests = requests.filter(r => r.status === 'Pushed to ModPack');
  const vestRequests = requests.filter(r => r.type === 'Vest');
  const helmetRequests = requests.filter(r => r.type === 'Helmet');

  // Calculate average time from creation to completion
  const getAverageCompletionTime = () => {
    const completed = requests.filter(r => 
      r.status === 'Pushed to ModPack' && 
      r.createdAt && 
      r.statusHistory?.['Pushed to ModPack']
    );
    
    if (completed.length === 0) return 'N/A';
    
    let totalHours = 0;
    completed.forEach(r => {
      const created = r.createdAt.toDate ? r.createdAt.toDate() : new Date(r.createdAt);
      const completed = r.statusHistory['Pushed to ModPack'].toDate 
        ? r.statusHistory['Pushed to ModPack'].toDate() 
        : new Date(r.statusHistory['Pushed to ModPack']);
      totalHours += (completed - created) / (1000 * 60 * 60);
    });
    
    const avgHours = totalHours / completed.length;
    if (avgHours < 24) return `${Math.round(avgHours)}h`;
    return `${Math.round(avgHours / 24)}d`;
  };

  // Get armorer stats
  const getArmorerStats = () => {
    const stats = {};
    
    armorers.forEach(armorer => {
      const assigned = requests.filter(r => r.assignedTo === armorer.id);
      const completed = assigned.filter(r => r.status === 'Pushed to ModPack');
      
      stats[armorer.id] = {
        name: armorer.username || armorer.displayName || armorer.email,
        assigned: assigned.length,
        completed: completed.length,
        inProgress: assigned.filter(r => r.status === 'In Progress').length,
        completionRate: assigned.length > 0 
          ? Math.round((completed.length / assigned.length) * 100) 
          : 0
      };
    });
    
    return Object.values(stats).sort((a, b) => b.completed - a.completed);
  };

  // Get status distribution
  const getStatusDistribution = () => {
    const statuses = ['Unassigned', 'In Progress', 'Ready for Config', 'Pushed to ModPack'];
    return statuses.map(status => ({
      status,
      count: requests.filter(r => r.status === status).length,
      percentage: requests.length > 0 
        ? Math.round((requests.filter(r => r.status === status).length / requests.length) * 100)
        : 0
    }));
  };

  // Get requests over time (last 7 days)
  const getRecentActivity = () => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + 1);
      
      const count = requests.filter(r => {
        if (!r.createdAt) return false;
        const created = r.createdAt.toDate ? r.createdAt.toDate() : new Date(r.createdAt);
        return created >= date && created < nextDate;
      }).length;
      
      days.push({
        date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        count
      });
    }
    return days;
  };

  const armorerStats = getArmorerStats();
  const statusDist = getStatusDistribution();
  const recentActivity = getRecentActivity();

  return (
    <div className="metrics-page" style={{ backgroundImage: `url(/images/C_SQD_Vol_V.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
      <nav className="metrics-navbar">
        <Link to="/" className="nav-left">
          <img src={unitLogo} alt="Logo" className="unit-logo" />
          <h1 className="site-title">Creature Supply</h1>
        </Link>
        <div className="nav-right">
          <Link to="/" className="nav-link">Home</Link>
          <Link to="/requests" className="nav-link">View Requests</Link>
          {isAdmin && <Link to="/admin" className="nav-link admin-nav">Admin</Link>}
          <button className="logout-btn" onClick={logout}>Logout</button>
        </div>
      </nav>

      <main className="metrics-content">
        <h2 className="page-title">Metrics Dashboard</h2>

        {loading ? (
          <div className="loading">Loading metrics...</div>
        ) : (
          <>
            {/* Overview Stats */}
            <div className="metrics-section">
              <h3>Overview</h3>
              <div className="overview-grid">
                <div className="metric-card">
                  <span className="metric-value">{requests.length}</span>
                  <span className="metric-label">Total Requests</span>
                </div>
                <div className="metric-card">
                  <span className="metric-value">{completedRequests.length}</span>
                  <span className="metric-label">Completed</span>
                </div>
                <div className="metric-card">
                  <span className="metric-value">{vestRequests.length}</span>
                  <span className="metric-label">Vest Requests</span>
                </div>
                <div className="metric-card">
                  <span className="metric-value">{helmetRequests.length}</span>
                  <span className="metric-label">Helmet Requests</span>
                </div>
                <div className="metric-card highlight">
                  <span className="metric-value">{getAverageCompletionTime()}</span>
                  <span className="metric-label">Avg Completion Time</span>
                </div>
              </div>
            </div>

            {/* Status Distribution */}
            <div className="metrics-section">
              <h3>Status Distribution</h3>
              <div className="status-bars">
                {statusDist.map(s => (
                  <div key={s.status} className="status-bar-item">
                    <div className="status-bar-label">
                      <span>{s.status}</span>
                      <span>{s.count} ({s.percentage}%)</span>
                    </div>
                    <div className="status-bar-track">
                      <div 
                        className={`status-bar-fill ${s.status.toLowerCase().replace(/\s+/g, '-')}`}
                        style={{ width: `${s.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Armorer Leaderboard */}
            <div className="metrics-section">
              <h3>Armorer Performance</h3>
              <div className="leaderboard">
                <table className="leaderboard-table">
                  <thead>
                    <tr>
                      <th>Armorer</th>
                      <th>Assigned</th>
                      <th>In Progress</th>
                      <th>Completed</th>
                      <th>Completion Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {armorerStats.map((stat, index) => (
                      <tr key={index}>
                        <td className="armorer-name">
                          {index === 0 && stat.completed > 0 && <span className="trophy">🏆</span>}
                          {stat.name}
                        </td>
                        <td>{stat.assigned}</td>
                        <td>{stat.inProgress}</td>
                        <td className="completed-count">{stat.completed}</td>
                        <td>
                          <span className={`rate ${stat.completionRate >= 75 ? 'high' : stat.completionRate >= 50 ? 'medium' : 'low'}`}>
                            {stat.completionRate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="metrics-section">
              <h3>Recent Activity (Last 7 Days)</h3>
              <div className="activity-chart">
                {recentActivity.map((day, index) => (
                  <div key={index} className="activity-day">
                    <div className="activity-bar-container">
                      <div 
                        className="activity-bar"
                        style={{ height: `${Math.max(day.count * 20, 5)}px` }}
                      ></div>
                    </div>
                    <span className="activity-count">{day.count}</span>
                    <span className="activity-date">{day.date}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default Metrics;
